import { useEffect, useMemo, useState } from "react"

import Button from "~/v2/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/v2/components/ui/dialog"
import { SRT_LANGUAGES } from "~/v2/constants/languages"
import type { VaasCaption } from "~/v2/types/vaas"

type VaasSrtLanguageDialogProps = {
  caption?: Partial<VaasCaption> | null
  captions?: VaasCaption[]
  onConfirm: (language: string) => void
  onOpenChange: (open: boolean) => void
  open: boolean
}

const VaasSrtLanguageDialog = ({
  caption,
  captions = [],
  onConfirm,
  onOpenChange,
  open,
}: VaasSrtLanguageDialogProps) => {
  const [languageValue, setLanguageValue] = useState("en")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    if (open) {
      setLanguageValue(caption?.srt_language || "en")
      setErrorMessage("")
    }
  }, [caption, open])

  const usedLanguages = useMemo(
    () => new Set((captions || []).map((item) => item.srt_language)),
    [captions],
  )

  const languageDisabled = (language: string) => {
    return usedLanguages.has(language) && !caption?.id
  }

  const handleConfirm = () => {
    if (languageDisabled(languageValue)) {
      setErrorMessage("This language is already added")
      return
    }

    onConfirm(languageValue)
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogClose />
        <DialogHeader>
          <DialogTitle>Add SRT Language</DialogTitle>
          <DialogDescription>
            Choose the language of the .srt file.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex flex-col gap-2">
          <select
            className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
            onChange={(event) => {
              setLanguageValue(event.target.value)
              setErrorMessage("")
            }}
            value={languageValue}
          >
            {Object.entries(SRT_LANGUAGES).map(([key, label]) => (
              <option disabled={languageDisabled(key)} key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          {errorMessage && (
            <div className="text-sm text-destructive">{errorMessage}</div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline">
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default VaasSrtLanguageDialog
